import React from "react";
import { Link } from "react-router-dom";
import { Crown, ArrowRight, Sparkles as SparklesIcon, ShieldCheck, Link as LinkIcon } from "lucide-react";
import Sparkles from "../components/Sparkles";
import LangToggle from "../components/LangToggle";
import { useLang } from "../lib/i18n";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };

export default function Scan() {
  const lang = useLang();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const fullUrl = origin + "/register";
  // Pretty-strip "https://" for friendlier display
  const prettyUrl = fullUrl.replace(/^https?:\/\//, "");

  return (
    <div className="relative min-h-screen w-full overflow-hidden flex flex-col items-center justify-center px-5 py-10 text-blush"
         style={{ background: "radial-gradient(circle at 50% 0%, #2c0e4f 0%, #160828 60%, #08041a 100%)" }}
         data-testid="scan-page">

      {/* Language toggle */}
      <div className="absolute top-3 right-3 z-20">
        <LangToggle light />
      </div>

      <Sparkles count={80} />

      {/* Star sprinkles top */}
      {[...Array(40)].map((_, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-[#FCF6BA] pointer-events-none"
          style={{
            width: 1.5 + (i % 3), height: 1.5 + (i % 3),
            left: `${(i * 71) % 100}%`, top: `${(i * 37) % 55}%`,
            opacity: 0.4 + ((i % 4) * 0.12),
            boxShadow: "0 0 4px rgba(252,246,186,0.85)",
          }}
        />
      ))}

      <div className="relative z-10 flex flex-col items-center text-center w-full max-w-md">
        <Crown className="text-[#FCF6BA] drop-shadow-2xl mb-3" size={54} />

        <p className="label-eyebrow tracking-[0.32em]" style={{ ...TE, color: "#D4AF37" }}>
          {lang === "te" ? "✦ స్వాగతం ✦" : "✦ WELCOME ✦"}
        </p>

        <h1
          className="gold-text-soft font-script drop-shadow-2xl mt-2 leading-tight"
          style={{ ...TE, fontSize: "clamp(36px, 9vw, 60px)" }}
        >
          {lang === "te" ? "అనఘ రాజ్యం" : "Anagha's Kingdom"}
        </h1>

        <p className="font-display italic text-blush/90 mt-2" style={{ ...TE, fontSize: "clamp(14px, 4vw, 18px)" }}>
          {lang === "te"
            ? "మా చిట్టి తల్లి మొదటి పుట్టినరోజు వేడుకలోకి మీకు స్వాగతం"
            : "Welcome to our Chitti Thalli's first birthday celebration"}
        </p>

        {/* The URL card — clearly visible so guests can verify it's safe */}
        <div
          className="mt-7 w-full rounded-2xl px-4 py-3 flex flex-col items-center gap-1"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,196,214,0.10) 100%)",
            border: "1px solid rgba(212,175,55,0.55)",
            boxShadow: "inset 0 0 24px rgba(212,175,55,0.15)",
          }}
          data-testid="scan-url-card"
        >
          <p className="label-eyebrow tracking-widest text-[#D4AF37]" style={TE}>
            {lang === "te" ? "మీరు ఈ వెబ్‌సైట్‌లో ఉన్నారు" : "YOU ARE ON"}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <ShieldCheck size={14} className="text-emerald-300 shrink-0" />
            <p className="font-mono text-blush break-all" style={{ fontSize: "clamp(12px, 3.6vw, 15px)" }}>
              {prettyUrl}
            </p>
          </div>
          <p className="text-blush/65 italic mt-1" style={{ ...TE, fontSize: "clamp(10px, 2.8vw, 12px)" }}>
            {lang === "te"
              ? "మా అధికారిక రాజ్య పేజీ — సురక్షితంగా కొనసాగండి"
              : "Our official kingdom page — safe to proceed"}
          </p>
        </div>

        {/* Big CTA — enter the kingdom (register flow) */}
        <Link
          to="/register"
          className="mt-6 inline-flex items-center justify-center gap-2 w-full rounded-full py-3.5 px-6 font-semibold transition-transform hover:scale-[1.02] active:scale-[0.98]"
          style={{
            background: "linear-gradient(135deg, #D4AF37 0%, #FFE89C 50%, #B8860B 100%)",
            color: "#3c0d3a",
            fontSize: "clamp(15px, 4.2vw, 18px)",
            boxShadow: "0 16px 40px rgba(212,175,55,0.55), inset 0 0 18px rgba(255,255,255,0.4)",
            ...TE,
          }}
          data-testid="scan-enter-btn"
        >
          <SparklesIcon size={16} />
          {lang === "te" ? "రాజ్యంలోకి ప్రవేశించండి" : "Enter the Kingdom"}
          <ArrowRight size={16} />
        </Link>

        {/* What happens next */}
        <div className="mt-5 text-left w-full" style={TE}>
          <p className="label-eyebrow text-[#FCF6BA] tracking-widest mb-2 text-center">
            {lang === "te" ? "✦ తర్వాత ఏం జరుగుతుంది ✦" : "✦ WHAT'S NEXT ✦"}
          </p>
          <ul className="space-y-2 text-blush/85" style={{ fontSize: "clamp(12px, 3.4vw, 14px)" }}>
            {[
              lang === "te" ? "1. మీ సెల్ఫీని అప్‌లోడ్ చేయండి (కెమెరా లేదా గ్యాలరీ)" : "1. Upload your selfie (camera or gallery)",
              lang === "te" ? "2. మీ పేరు మరియు అనఘతో సంబంధాన్ని చెప్పండి" : "2. Tell us your name and relationship to Anagha",
              lang === "te" ? "3. మీ ముఖంతో రాజ్య నక్షత్రంగా ప్రొజెక్టర్‌లో మెరుస్తారు ✨" : "3. Shine as a star on the projector with your own face ✨",
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-[#D4AF37] mt-0.5">✦</span>
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Tiny footer URL chip for share/copy */}
        <button
          onClick={() => {
            navigator.clipboard?.writeText(fullUrl).then(() => {
              // tiny toast-like flash via title attr (browsers vary)
            }).catch(() => {});
          }}
          className="mt-6 inline-flex items-center gap-1.5 text-blush/70 hover:text-blush text-xs px-3 py-1.5 rounded-full border border-blush/20 hover:bg-white/5 transition-colors"
          data-testid="scan-copy-url"
          title="Copy URL"
        >
          <LinkIcon size={11} /> {lang === "te" ? "లింక్ కాపీ చేయండి" : "Copy link"}
        </button>
      </div>
    </div>
  );
}
